import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from 'src/apiConfig';
import { useTranslation } from 'react-i18next';
import { latinToCyrillic } from 'src/helper/helperFunction';

/* DIALOG ZA DODAVANJE SKOLE */

const AddSchoolDialog = ({
  open,
  handleClose,
  schoolData,
  onSuccess,
  locations
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [street, setStreet] = useState('');
  const [telephone, setTelephone] = useState('');
  const [email, setEmail] = useState('');
  const [professor, setProfessor] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (schoolData) {
      setName(schoolData.name);
      setSelectedLocation(
        locations.find((location) => location.zipcode === schoolData.zipcode)
      );
      setStreet(schoolData.street);
      setTelephone(schoolData.telephone);
      setEmail(schoolData.email);
      setProfessor(schoolData.professor);
    } else {
      setName('');
      setSelectedLocation(null);
      setStreet('');
      setTelephone('');
      setEmail('');
      setProfessor('');
    }
    setErrors({});
  }, [schoolData, locations]);

  useEffect(() => {
    if (!open) {
      setName('');
      setSelectedLocation(null);
      setStreet('');
      setTelephone('');
      setEmail('');
      setProfessor('');
      setErrors({});
    }
  }, [open]);
  const validate = () => {
    const newErrors = {};
    if (!name) newErrors.name = t('errors.required');
    if (!selectedLocation) newErrors.location = t('errors.required');
    if (!telephone) newErrors.telephone = t('errors.required');
    if (!email) {
      newErrors.email = t('errors.required');
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = t('errors.invalidEmail');
    }
    if (!professor) newErrors.professor = t('errors.required');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const schoolDetails = {
      name,
      zipcode: selectedLocation?.zipcode || '',
      street,
      telephone,
      email,
      professor
    };

    try {
      if (schoolData) {
        await axios.put(
          `${API_BASE_URL}/api/schools.php?id=${schoolData.id}`,
          schoolDetails
        );
      } else {
        await axios.post(`${API_BASE_URL}/api/schools.php`, schoolDetails);
      }
      onSuccess();
      setName('');
      setSelectedLocation(null);
      setStreet('');
      setTelephone('');
      setEmail('');
      setProfessor('');
      handleClose();
    } catch (error) {
      console.error('Error saving school:', error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          padding: '20px',
          backgroundColor: '#ffffff',
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)'
        }
      }}
    >
      <DialogTitle
        sx={{
          fontSize: '1.5rem',
          fontWeight: 'bold',
          textAlign: 'center',
          color: '#2596be'
        }}
      >
        {schoolData ? t('dialogs.editSchool') : t('adminschool.addNew')}
      </DialogTitle>

      <DialogContent sx={{ padding: '20px', gap: '15px' }}>
        <TextField
          fullWidth
          margin="dense"
          label={t('columns.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={!!errors.name}
          helperText={errors.name}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px'
            },
            '& .MuiFormLabel-root': {
              color: '#2596be'
            }
          }}
        />

        <Autocomplete
          options={locations}
          getOptionLabel={(option) => option.name}
          value={selectedLocation}
          onChange={(event, newValue) => setSelectedLocation(newValue)}
          filterOptions={(options, state) => {
            const searchTerm = state.inputValue.toLowerCase();
            const cyrillicSearchTerm = latinToCyrillic(searchTerm);

            return options.filter(
              (option) =>
                option.name.toLowerCase().includes(searchTerm) ||
                option.name.toLowerCase().includes(cyrillicSearchTerm)
            );
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.zipcode}>
              {option.name}
            </li>
          )}
          noOptionsText={t('pagination.noRowsLabel')}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('columns.location')}
              margin="dense"
              fullWidth
              error={!!errors.location}
              helperText={errors.location}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                },
                '& .MuiFormLabel-root': {
                  color: '#2596be'
                }
              }}
            />
          )}
        />

        <TextField
          fullWidth
          margin="dense"
          label={t('columns.street')}
          value={street}
          onChange={(e) => setStreet(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px'
            },
            '& .MuiFormLabel-root': {
              color: '#2596be'
            }
          }}
        />

        <TextField
          fullWidth
          margin="dense"
          label={t('columns.telephone')}
          value={telephone}
          onChange={(e) => setTelephone(e.target.value)}
          error={!!errors.telephone}
          helperText={errors.telephone}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px'
            },
            '& .MuiFormLabel-root': {
              color: '#2596be'
            }
          }}
        />

        <TextField
          fullWidth
          margin="dense"
          label={t('columns.email')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={!!errors.email}
          helperText={errors.email}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px'
            },
            '& .MuiFormLabel-root': {
              color: '#2596be'
            }
          }}
        />

        <TextField
          fullWidth
          margin="dense"
          label={t('columns.professor')}
          value={professor}
          onChange={(e) => setProfessor(e.target.value)}
          error={!!errors.professor}
          helperText={errors.professor}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px'
            },
            '& .MuiFormLabel-root': {
              color: '#2596be'
            }
          }}
        />
      </DialogContent>

      <DialogActions sx={{ padding: '20px', justifyContent: 'space-between' }}>
        <Button
          onClick={handleClose}
          sx={{
            backgroundColor: '#e0e0e0',
            color: '#333',
            borderRadius: '8px',
            padding: '8px 16px',
            fontSize: '0.875rem',
            '&:hover': {
              backgroundColor: '#d5d5d5'
            }
          }}
        >
          {t('dialog.cancelButton')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            backgroundColor: '#2596be',
            color: '#fff',
            borderRadius: '8px',
            padding: '8px 16px',
            fontWeight: 'bold',
            fontSize: '0.875rem',
            '&:hover': {
              backgroundColor: '#1976d2'
            }
          }}
        >
          {t('adminModovi.saveChanges')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddSchoolDialog;
